import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { slugify, type ComposerState } from './types';

import { useAuth } from '@/lib/auth/useAuth';
import { supabase } from '@/lib/supabase';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseArticleComposerArgs {
  initialContentId?: string;
  initial: ComposerState;
  initialStatus?: string;
}

const AUTOSAVE_DEBOUNCE_MS = 2000;
const MAX_RETRIES = 3;

export function useArticleComposer({
  initialContentId,
  initial,
  initialStatus,
}: UseArticleComposerArgs) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [state, setState] = useState<ComposerState>(initial);
  const [contentId, setContentId] = useState<string | undefined>(initialContentId);
  const [status, setStatus] = useState<string>(initialStatus ?? 'draft');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);

  const debounceRef = useRef<number | null>(null);
  const retryRef = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;
  const contentIdRef = useRef(contentId);
  contentIdRef.current = contentId;
  const statusRef = useRef(status);
  statusRef.current = status;

  const patch = useCallback((p: Partial<ComposerState>) => {
    setState((prev) => ({ ...prev, ...p }));
    setDirty(true);
  }, []);

  // Core persistence. Creates the content row on first save, then snapshots a
  // version. Published rows stay published (status not downgraded on edit).
  const persist = useCallback(
    async (isAutosave: boolean): Promise<void> => {
      if (!user) return;
      const s = stateRef.current;
      const slug = s.slug || slugify(s.title) || `draft-${Date.now()}`;
      setSaveStatus('saving');
      try {
        let id = contentIdRef.current;
        const fields = {
          title: s.title || 'Untitled',
          excerpt: s.excerpt || null,
          body_json: s.bodyJson,
          // Write only the canonical reference; the DB trigger populates the
          // cover_image_url/alt cache from the asset (Spec 41).
          cover_asset_id: s.coverAssetId,
          tradition_tags: s.traditionTags,
          topic_tags: s.topicTags,
          gate_policy: s.gatePolicy,
          access_tier: s.accessTier,
        };

        if (!id) {
          const { data, error } = await supabase
            .from('content')
            .insert({
              creator_id: user.id,
              type: 'article',
              slug,
              status: 'draft',
              ...fields,
            })
            .select('id')
            .single();
          if (error) throw error;
          id = data.id;
          setContentId(id);
          contentIdRef.current = id;
        } else {
          // Published rows: don't touch slug (DB trigger also enforces this).
          const updatePayload = statusRef.current === 'published' ? fields : { slug, ...fields };
          const { error } = await supabase.from('content').update(updatePayload).eq('id', id);
          if (error) throw error;
        }

        // Next version number.
        const { data: maxRow } = await supabase
          .from('content_versions')
          .select('version_number')
          .eq('content_id', id)
          .order('version_number', { ascending: false })
          .limit(1)
          .maybeSingle();
        const nextVersion = (maxRow?.version_number ?? 0) + 1;

        const { error: versionError } = await supabase.from('content_versions').insert({
          content_id: id,
          version_number: nextVersion,
          title: s.title,
          excerpt: s.excerpt,
          body_json: s.bodyJson,
          // Snapshot the reference (so restore keeps the cover) plus the cached
          // url/alt for at-a-glance display of the version.
          cover_asset_id: s.coverAssetId,
          cover_image_url: s.coverImageUrl,
          cover_image_alt: s.coverImageAlt,
          tradition_tags: s.traditionTags,
          topic_tags: s.topicTags,
          gate_policy: s.gatePolicy,
          saved_by: user.id,
          is_autosave: isAutosave,
        });
        if (versionError) throw versionError;

        retryRef.current = 0;
        setSaveStatus('saved');
        setLastSavedAt(new Date());
        setDirty(false);
        void queryClient.invalidateQueries({ queryKey: ['content-versions', id] });
        void queryClient.invalidateQueries({ queryKey: ['content', 'creator', user.id] });
      } catch (err) {
        retryRef.current += 1;
        setSaveStatus('error');
        if (retryRef.current <= MAX_RETRIES) {
          const backoff = 1000 * 2 ** retryRef.current;
          window.setTimeout(() => void persist(isAutosave), backoff);
        }
        throw err;
      }
    },
    [user, queryClient],
  );

  // Debounced autosave on dirty state.
  useEffect(() => {
    if (!dirty) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void persist(true).catch(() => {});
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [dirty, state, persist]);

  const manualSave = useCallback(async () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    await persist(false).catch(() => {});
  }, [persist]);

  // Cmd/Ctrl+S manual save.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void manualSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [manualSave]);

  const submitForReview = useCallback(async () => {
    if (!contentIdRef.current) await persist(false);
    const id = contentIdRef.current;
    if (!id) throw new Error('Save before submitting');
    const { error } = await supabase
      .from('content')
      .update({ status: 'pending_review' })
      .eq('id', id);
    if (error) throw error;
    setStatus('pending_review');
    void queryClient.invalidateQueries({ queryKey: ['content', 'creator', user?.id] });
  }, [persist, queryClient, user?.id]);

  return {
    state,
    patch,
    setState: (next: ComposerState) => {
      setState(next);
      setDirty(true);
    },
    contentId,
    status,
    saveStatus,
    lastSavedAt,
    dirty,
    manualSave,
    submitForReview,
  };
}
